
/**
 * Module dependencies.
 */

import { Download, DownloadCategory } from 'src/types/downloads';
import { categoryNameToSlug } from 'src/core/utils/category-name-slug';
import { color, media, units } from 'src/styles/utils';
import { fetchDownloadCategories } from 'src/api/app/downloads/fetch-download-categories';
import { fetchInstitutionalDownloads } from 'src/api/app/downloads/fetch-institutional-downloads';
import { fetchPage } from 'src/api/app/pages/fetch-page';
import { getApiPageSlugByLocale } from 'src/core/utils/routes';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { ifProp, theme } from 'styled-tools';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import DownloadCard from 'src/components/cards/download-card';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import Type from 'src/components/core/typography';
import filter from 'lodash/filter';
import head from 'lodash/head';
import map from 'lodash/map';
import omit from 'lodash/omit';
import searchIcon from 'src/assets/svg/search.svg';
import styled, { createGlobalStyle, css } from 'styled-components';
import useDownloadCategories from 'src/api/app/downloads/use-download-categories';
import useInstitutionalDownloads from 'src/api/app/downloads/use-institutional-downloads';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';
import values from 'lodash/values';

/**
 * `PageProps` type.
 */

type PageProps = {
  descriptionSeo?: string | null,
  id: string,
  slug: string,
  subtitle?: string | null,
  title: string,
  titleSeo?: string | null,
  translations: Record<string, unknown>[]
};

/**
 * `Props` type.
 */

type Props = {
  categories: DownloadCategory[],
  data: PageProps,
  downloads: Download[],
  initialCategorySlug: string | null,
  slug: string
};

/**
 * Read `category` query param as a slug string.
 */

function parseCategorySlugFromQuery(query: { category?: string | string[] }): string | null {
  const raw = query.category;

  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }

  if (Array.isArray(raw) && raw[0]) {
    return String(raw[0]).trim() || null;
  }

  return null;
}

/**
 * `GlobalStyle` component.
 */

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${color('white')};
  }
`;

/**
 * `Section` styled component.
 */

const Section = styled.section<{ isLoading: boolean }>`
  background-color: ${color('white')};
  padding: ${theme('dimensions.navbarHeightMobile')}px 0 ${units(10)};
  position: relative;

  ${ifProp('isLoading', css`
    min-height: 100vh;
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title    .'
      '. subTitle .'
      '. filters  .'
      '. list     .';
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)`
  grid-area: title;
  padding-top: ${units(10)};
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H5).attrs({ as: 'h2' })`
  grid-area: subTitle;
  margin-bottom: ${units(6)};
  padding-top: ${units(1)};

  ${media.min('ms')`
    padding-top: ${units(2)};
  `}
`;

/**
 * `FiltersBar` styled component.
 */

const FiltersBar = styled.div<{ $noSubtitle?: boolean }>`
  grid-area: filters;
  margin-bottom: ${units(8)};
  overflow: hidden;

  ${ifProp('$noSubtitle', css`
    margin-top: ${units(6)};
  `)}
  position: relative;

  &::after {
    background-image: linear-gradient(to right, ${color.transparentize('white', 0)}, ${color('white')} 100%);
    content: '';
    height: ${units(7)};
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    width: ${units(7)};
  }

  ${media.min('md')`
    overflow: visible;

    &::after {
      display: none;
    }
  `}
`;

/**
 * `FiltersInner` styled component.
 */

const FiltersInner = styled.div`
  ${media.min('md')`
    align-items: flex-start;
    display: flex;
    gap: ${units(3)};
  `}
`;

/**
 * `Categories` styled component.
 */

const Categories = styled.div`
  display: grid;
  grid-auto-columns: min-content;
  grid-auto-flow: column;
  grid-gap: ${units(2)};
  overflow-x: auto;
  padding-bottom: ${units(2)};
  scroll-snap-type: x mandatory;

  ${media.min('md')`
    display: flex;
    flex: 1 1 auto;
    flex-flow: row wrap;
    gap: ${units(2)};
    min-width: 0;
    overflow: visible;
    padding-bottom: 0;
  `}
`;

/**
 * `SearchWrapper` styled component.
 */

const SearchWrapper = styled.div`
  margin-top: ${units(2)};

  ${media.min('md')`
    flex-shrink: 0;
    margin-top: 0;
  `}
`;

/**
 * `SearchInputWrapper` styled component.
 */

const SearchInputWrapper = styled.div`
  margin-top: ${units(3)};
`;

/**
 * `SearchInput` styled component.
 */

const SearchInput = styled.input`
  background: transparent;
  border: none;
  border-bottom: 1px solid ${color('grey400')};
  color: ${color('textColor')};
  font-size: 14px;
  letter-spacing: 1px;
  outline: none;
  padding: ${units(1)} 0;
  text-transform: uppercase;
  width: 100%;

  &::placeholder {
    color: ${color('grey500')};
  }

  ${media.min('md')`
    max-width: ${units(40)};
  `}
`;

/**
 * `SectionTitle` styled component.
 */

const SectionTitle = styled(Type.H5).attrs({ as: 'h3' })`
  margin-bottom: ${units(5)};
`;

/**
 * `ListWrapper` styled component.
 */

const ListWrapper = styled.section`
  grid-area: list;
  min-height: 250px;
  position: relative;
`;

/**
 * `CardGrid` styled component.
 */

const CardGrid = styled.div`
  align-items: stretch;
  column-gap: ${units(4)};
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: ${units(4)};

  ${media.min('ms')`
    column-gap: ${units(3)};
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  ${media.min('md')`
    column-gap: ${units(5)};
    grid-template-columns: repeat(3, minmax(0, 1fr));
    row-gap: ${units(6)};
  `}

  ${media.min('xl')`
    column-gap: ${units(5)};
    grid-template-columns: repeat(4, minmax(0, 1fr));
    row-gap: ${units(5)};
  `}
`;

/**
 * `CardGridItem` stretches each cell so row heights match the tallest card.
 */

const CardGridItem = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;

  > * {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }
`;

/**
 * `Empty` styled component.
 */

const Empty = styled.div`
  text-align: center;
`;

/**
 * `Downloads` page.
 */

const Downloads = (props: Props): ReactElement => {
  const router = useRouter();
  const { locale } = router;
  const { translate } = useTranslate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setSearchOpen] = useState(false);

  const {
    data: pageData,
    isLoading: isLoadingPage,
    isSuccess: isSuccessPage
  } = usePage<PageProps>({
    initialData: props.data,
    slug: props.slug
  });

  const {
    data: categories,
    isSuccess: isCategoriesSuccess
  } = useDownloadCategories<DownloadCategory[]>({ initialData: props.categories });

  const {
    data: downloads,
    isLoading: isDownloadsLoading,
    isSuccess: isDownloadsSuccess
  } = useInstitutionalDownloads<Download[]>({ initialData: props.downloads });

  const isLoading = isLoadingPage || isDownloadsLoading;
  const isSuccess = isSuccessPage && isCategoriesSuccess && isDownloadsSuccess;
  const categoriesList = useMemo(() => {
    return Array.isArray(categories) ? categories : values(categories ?? {});
  }, [categories]);

  const downloadsList = useMemo(() => {
    return Array.isArray(downloads) ? downloads : values(downloads ?? {});
  }, [downloads]);

  const categoriesWithDownloads = useMemo(() => {
    return filter(categoriesList, ({ id }) => {
      return downloadsList.some(({ category }) => category?.id === id);
    });
  }, [categoriesList, downloadsList]);

  const categoryBySlug = useMemo(() => {
    const map = new Map<string, DownloadCategory>();

    categoriesWithDownloads.forEach(category => {
      const slug = categoryNameToSlug(category.name);

      if (!map.has(slug)) {
        map.set(slug, category);
      }
    });

    return map;
  }, [categoriesWithDownloads]);

  const slugFromUrl = useMemo(() => {
    if (!router.isReady) {
      return props.initialCategorySlug;
    }

    return parseCategorySlugFromQuery(router.query);
  }, [props.initialCategorySlug, router.isReady, router.query]);

  const activeCategory = useMemo(() => {
    if (slugFromUrl) {
      const found = categoryBySlug.get(slugFromUrl);

      if (found) {
        return found;
      }
    }

    return head(categoriesWithDownloads) ?? null;
  }, [categoriesWithDownloads, categoryBySlug, slugFromUrl]);

  useEffect(() => {
    if (!router.isReady || !slugFromUrl) {
      return;
    }

    if (categoryBySlug.has(slugFromUrl)) {
      return;
    }

    router.replace(
      { pathname: router.pathname, query: omit(router.query, 'category') },
      undefined,
      { shallow: true }
    );
  }, [categoryBySlug, router, slugFromUrl]);

  const handleCategoryClick = useCallback(
    (category: DownloadCategory) => {
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, category: categoryNameToSlug(category.name) }
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  const filteredDownloads = useMemo(() => {
    const categoryId = activeCategory?.id;
    const base = categoryId
      ? filter(downloadsList, ({ category }) => category?.id === categoryId)
      : downloadsList;

    const trimmed = searchQuery.trim();
    const matched = trimmed
      ? filter(base, ({ name }) => name.toLowerCase().includes(trimmed.toLowerCase()))
      : base;

    return [...matched].sort((left, right) =>
      left.name.localeCompare(right.name, locale, { sensitivity: 'base' })
    );
  }, [activeCategory, downloadsList, locale, searchQuery]);

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <GlobalStyle />

      <Metatags
        description={pageData?.descriptionSeo}
        hrefLangs={getHrefLangs(pageData?.translations, 'downloads')}
        pageTitle={pageData?.title}
        title={pageData?.titleSeo}
      />

      <Section isLoading={isLoading}>
        <Loading active={isLoading} />

        {isSuccess && (
          <Container>
            <Grid>
              <Title>
                <FadeInUpAnimation>
                  {`${pageData?.title} `}
                </FadeInUpAnimation>
              </Title>

              {pageData?.subtitle && (
                <SubTitle>
                  <FadeInUpAnimation>
                    {pageData.subtitle}
                  </FadeInUpAnimation>
                </SubTitle>
              )}

              <FiltersBar $noSubtitle={!pageData?.subtitle}>
                <FiltersInner>
                  <Categories>
                    {map(categoriesWithDownloads, category => (
                      <Button
                        key={category.id}
                        onClick={() => handleCategoryClick(category)}
                        variant={activeCategory?.id === category.id ? 'fill' : 'outlined'}
                      >
                        {category.name}
                      </Button>
                    ))}
                  </Categories>

                  <SearchWrapper>
                    <Button
                      aria-controls={'downloads-page-search'}
                      aria-expanded={isSearchOpen}
                      icon={searchIcon}
                      onClick={() => setSearchOpen(open => !open)}
                      variant={'outlined'}
                    >
                      {translate('common:actions.search')}
                    </Button>
                  </SearchWrapper>
                </FiltersInner>

                {isSearchOpen && (
                  <SearchInputWrapper>
                    <SearchInput
                      aria-label={translate('common:actions.search')}
                      autoFocus
                      id={'downloads-page-search'}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder={translate('common:actions.search')}
                      type={'text'}
                      value={searchQuery}
                    />
                  </SearchInputWrapper>
                )}
              </FiltersBar>

              <ListWrapper>
                {activeCategory && (
                  <FadeInUpAnimation>
                    <SectionTitle>
                      {activeCategory.name}
                    </SectionTitle>
                  </FadeInUpAnimation>
                )}

                {filteredDownloads.length === 0 ? (
                  <Empty>
                    <Type.H6 paddingVertical={units(1)}>
                      <FadeInUpAnimation>
                        {translate('collections:list.emptyList')}
                      </FadeInUpAnimation>
                    </Type.H6>
                  </Empty>
                ) : (
                  <CardGrid>
                    {map(filteredDownloads, (download: Download, index: number) => (
                      <CardGridItem key={download.id ?? index}>
                        <FadeInUpAnimation>
                          <DownloadCard
                            downloadUrl={download.downloadUrl}
                            imageUrl={download.imageUrl}
                            name={download.name}
                          />
                        </FadeInUpAnimation>
                      </CardGridItem>
                    ))}
                  </CardGrid>
                )}
              </ListWrapper>
            </Grid>
          </Container>
        )}
      </Section>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export async function getServerSideProps({ locale, query }) {
  const slug = getApiPageSlugByLocale('downloads', locale);
  const categories = await fetchDownloadCategories({ locale });
  const pageData = await fetchPage({
    hasPreview: query?.preview,
    locale,
    slug
  });

  if (!pageData && !categories) {
    return {
      notFound: true
    };
  }

  const initialCategorySlug = parseCategorySlugFromQuery(query);

  return {
    props: {
      categories,
      data: pageData,
      downloads: await fetchInstitutionalDownloads({ locale }),
      initialCategorySlug,
      slug
    }
  };
}

/**
 * Export `Downloads` page.
 */

export default Downloads;
